import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Search, Download, Banknote, TrendingUp, CheckCircle2, Clock, Minus, RotateCcw, Calendar, ChevronDown, Filter, MoreVertical, Eye, EyeOff } from 'lucide-react';
import { useColumnVisibility, ColumnDef } from '../hooks/useColumnVisibility';
import ColumnVisibilityMenu from '../components/ColumnVisibilityMenu';

/**
 * A transaction record as returned by `GET /api/v1/hq/transactions`.
 * The `users` relation is joined server-side from the Supabase `users` table.
 */
interface Transaction {
    /** UUID primary key of the transaction. */
    id: string;
    /** UUID of the owning user (FK → users.id). */
    user_id: string;
    /** Transaction amount in the user's local currency (INR). */
    amount: number;
    /** Whether the transaction is income or an expense. Case may vary (legacy rows use uppercase). */
    type: 'income' | 'expense' | 'INCOME' | 'EXPENSE';
    /** Logical date of the transaction (YYYY-MM-DD). */
    date: string;
    /** ISO 8601 timestamp of when the row was inserted. Used for display ordering. */
    created_at: string;
    /** Optional human-readable description entered by the user. */
    description: string | null;
    /** Origin of the transaction: 'manual' or 'sms'. */
    source: string | null;
    /** Joined user profile. Null if the user row no longer exists. */
    users: {
        email: string | null;
        display_name: string | null;
    } | null;
}

/**
 * Formats an ISO 8601 timestamp into a human-readable date-time string.
 *
 * @example
 * formatDate('2024-10-24T14:21:00Z') // → 'Oct 24, 2024, 14:21'
 *
 * @param dateStr - ISO 8601 date string (e.g. from `created_at`).
 * @returns Formatted string like "Oct 24, 2024, 14:21".
 */
function formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) +
        ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * Returns a display name for the user who owns the transaction.
 * Falls back progressively: display_name → email → first 8 chars of user_id.
 *
 * @param tx - Transaction row with an optional joined `users` relation.
 * @returns A human-readable string identifying the user.
 */
function getCustomerName(tx: Transaction): string {
    return tx.users?.display_name || tx.users?.email || tx.user_id.slice(0, 8);
}

/**
 * Derives up to two uppercase initials for the user avatar.
 * Logic: first letters of each word in display_name/email, or first 2 chars of user_id as a last resort.
 *
 * @param tx - Transaction row with an optional joined `users` relation.
 * @returns 1–2 uppercase characters suitable for an avatar placeholder.
 */
function getCustomerInitials(tx: Transaction): string {
    const name = tx.users?.display_name || tx.users?.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    return tx.user_id.slice(0, 2).toUpperCase();
}

/**
 * Maps a transaction type to a Tailwind color token used for badge and avatar styling.
 *
 * @param type - Transaction type string ('income'/'INCOME' → emerald, anything else → rose).
 * @returns `'emerald'` for income, `'rose'` for expense.
 */
function getTypeColor(type: string): 'emerald' | 'rose' {
    return type.toLowerCase() === 'income' ? 'emerald' : 'rose';
}

/**
 * Returns Tailwind CSS classes for the transaction type badge (pill).
 *
 * @param color - Color token returned by {@link getTypeColor}.
 * @returns A string of Tailwind utility classes.
 */
function getStatusStyles(color: 'emerald' | 'rose') {
    if (color === 'emerald') return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
    return 'bg-rose-500/10 text-rose-400 border border-rose-500/20';
}

/**
 * Returns Tailwind CSS classes for the circular user avatar.
 *
 * @param color - Color token returned by {@link getTypeColor}.
 * @returns A string of Tailwind utility classes.
 */
function getAvatarStyles(color: 'emerald' | 'rose') {
    if (color === 'emerald') return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
    return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
}

/**
 * Formats a numeric amount as an Indian Rupee string with 2 decimal places.
 *
 * @example
 * formatAmount(1250) // → '₹1,250.00'
 *
 * @param amount - Raw numeric amount from the database.
 * @returns Formatted string prefixed with the ₹ symbol.
 */
function formatAmount(amount: number): string {
    return '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Number of transaction rows fetched per page from the API. */
const PAGE_LIMIT = 20;

/** localStorage key the Transactions table's column preference is persisted under. */
const COLUMNS_STORAGE_KEY = 'hq-transactions-columns';

/**
 * Columns rendered in the Transactions table, in display order. `key` values
 * are persisted to localStorage as part of {@link COLUMNS_STORAGE_KEY} — never
 * rename one without a migration. `id` is required (the row's only stable
 * identifier), everything else defaults to visible but can be hidden from the
 * "Columns" menu.
 */
const TRANSACTIONS_TABLE_COLUMNS: ColumnDef[] = [
    { key: 'id', label: 'Transaction ID', required: true },
    { key: 'date', label: 'Date' },
    { key: 'user', label: 'User' },
    { key: 'type', label: 'Type' },
    { key: 'amount', label: 'Amount' },
    { key: 'action', label: 'Action' },
];

/**
 * HQ admin page that displays a paginated table of all user transactions.
 *
 * Fetches real data from `GET /api/v1/hq/transactions` (requires HQ admin auth).
 * Renders skeleton rows while loading and supports page-based navigation.
 */
export default function HQTransactions() {
    const { logout, currentUser, getToken } = useAuth();

    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showNumbers, setShowNumbers] = useState(false);

    const { isVisible: isColumnVisible, toggle: toggleColumn, resetToDefault: resetColumns } = useColumnVisibility(COLUMNS_STORAGE_KEY, TRANSACTIONS_TABLE_COLUMNS);
    const visibleColumnCount = TRANSACTIONS_TABLE_COLUMNS.filter((c) => isColumnVisible(c.key)).length;

    /**
     * Fetches a single page of transactions from the HQ API and updates state.
     * Attaches the current user's Firebase ID token for authentication.
     *
     * @param pageNum - 1-based page number to fetch.
     */
    const fetchTransactions = useCallback(async (pageNum: number) => {
        setLoading(true);
        setError('');
        try {
            const token = await getToken();
            if (!token) return;

            const res = await fetch(`/api/v1/hq/transactions?page=${pageNum}&limit=${PAGE_LIMIT}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch transactions');

            setTransactions(json.data.transactions);
            setTotal(json.data.total);
            setTotalPages(json.data.totalPages);
        } catch (err: any) {
            console.error('HQTransactions fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchTransactions(page);
    }, [fetchTransactions, page]);

    const pageNumbers = (() => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
        if (page >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, '...', page - 1, page, page + 1, '...', totalPages];
    })();

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
            {/* Sidebar */}
            <div className="w-64 border-r border-zinc-800 p-6 flex flex-col gap-8">
                <div className="font-bold text-xl text-yellow-400">HQ AppPulse</div>

                <nav className="flex flex-col gap-2 flex-grow">
                    <Link to="/hq/dashboard" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Dashboard
                    </Link>
                    <Link to="/hq/insights" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        User Insights
                    </Link>
                    <div className="flex items-center gap-3 px-4 py-3 bg-zinc-900 text-yellow-400 rounded-xl font-medium">
                        Transactions
                    </div>
                    <Link to="/hq/notifications" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Notifications
                    </Link>
                    <a href="#" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Reports
                    </a>
                </nav>

                <div className="flex flex-col gap-4 border-t border-zinc-800 pt-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700"></div>
                        <div className="overflow-hidden">
                            <div className="text-sm font-bold truncate max-w-[120px]">{currentUser?.email}</div>
                            <div className="text-xs text-zinc-500">HQ Access</div>
                        </div>
                    </div>
                    <button onClick={logout} className="text-sm text-left text-zinc-500 hover:text-red-400 transition-colors px-2">
                        Sign out
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-10 overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold mb-1">Transactions</h1>
                        <p className="text-sm text-zinc-500">Manage and audit your financial activities</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative hidden sm:block">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input className="pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-sm focus:outline-none focus:border-yellow-400/50 focus:ring-1 focus:ring-yellow-400/50 w-64 text-zinc-100 placeholder-zinc-600 transition-all" placeholder="Search transaction ID..." type="text" />
                        </div>
                        <ColumnVisibilityMenu
                            columns={TRANSACTIONS_TABLE_COLUMNS}
                            isVisible={isColumnVisible}
                            onToggle={toggleColumn}
                            onReset={resetColumns}
                        />
                        <button
                            onClick={() => setShowNumbers(prev => !prev)}
                            className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-zinc-300"
                        >
                            {showNumbers ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            {showNumbers ? 'Hide Numbers' : 'Show Numbers'}
                        </button>
                        <button className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-zinc-300">
                            <Download className="w-5 h-5" />
                            Export
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl mb-8">
                        {error}
                    </div>
                )}

                {/* Main Transaction Section */}
                <div className="bg-zinc-900 rounded-2xl border border-zinc-800 shadow-sm overflow-hidden flex flex-col">
                    {/* Filters */}
                    <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 gap-2 text-sm font-medium hover:bg-zinc-800 transition-colors cursor-pointer text-zinc-300">
                                <Filter className="w-4 h-4 text-zinc-500" />
                                All Types
                                <ChevronDown className="w-4 h-4 text-zinc-500" />
                            </div>
                        </div>
                        <div className="text-sm text-zinc-500">
                            {loading ? 'Loading...' : `Showing ${(page - 1) * PAGE_LIMIT + 1}–${Math.min(page * PAGE_LIMIT, total)} of ${total.toLocaleString()} transactions`}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-zinc-800 bg-zinc-950/50">
                                    {isColumnVisible('id') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">Transaction ID</th>}
                                    {isColumnVisible('date') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">Date</th>}
                                    {isColumnVisible('user') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">User</th>}
                                    {isColumnVisible('type') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider">Type</th>}
                                    {isColumnVisible('amount') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Amount</th>}
                                    {isColumnVisible('action') && <th className="px-6 py-4 text-xs font-bold text-zinc-500 uppercase tracking-wider text-right">Action</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800">
                                {loading ? (
                                    Array.from({ length: PAGE_LIMIT }).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            {isColumnVisible('id') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-32" /></td>}
                                            {isColumnVisible('date') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-36" /></td>}
                                            {isColumnVisible('user') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-28" /></td>}
                                            {isColumnVisible('type') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-16" /></td>}
                                            {isColumnVisible('amount') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-20 ml-auto" /></td>}
                                            {isColumnVisible('action') && <td className="px-6 py-4"><div className="h-4 bg-zinc-800 rounded w-4 ml-auto" /></td>}
                                        </tr>
                                    ))
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColumnCount} className="px-6 py-12 text-center text-zinc-500 italic">No transactions found</td>
                                    </tr>
                                ) : (
                                    transactions.map((tx) => {
                                        const color = getTypeColor(tx.type);
                                        return (
                                            <tr key={tx.id} className="hover:bg-zinc-800/50 transition-colors">
                                                {isColumnVisible('id') && <td className="px-6 py-4 text-sm font-mono text-zinc-300">{tx.id.slice(0, 8).toUpperCase()}</td>}
                                                {isColumnVisible('date') && <td className="px-6 py-4 text-sm text-zinc-400">{formatDate(tx.created_at)}</td>}
                                                {isColumnVisible('user') && (
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${getAvatarStyles(color)}`}>
                                                                {getCustomerInitials(tx)}
                                                            </div>
                                                            <span className="text-sm font-medium text-zinc-200">{getCustomerName(tx)}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {isColumnVisible('type') && (
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusStyles(color)}`}>
                                                                {tx.type.charAt(0).toUpperCase() + tx.type.slice(1).toLowerCase()}
                                                            </span>
                                                            <span className="bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide">
                                                                {tx.source === 'sms' ? 'AUTO' : 'MANUAL'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                )}
                                                {isColumnVisible('amount') && (
                                                    <td className="px-6 py-4 text-sm font-bold text-right text-zinc-100">
                                                        {showNumbers ? formatAmount(tx.amount) : '₹ ••••'}
                                                    </td>
                                                )}
                                                {isColumnVisible('action') && (
                                                    <td className="px-6 py-4 text-right">
                                                        <button className="text-zinc-500 hover:text-yellow-400 transition-colors">
                                                            <MoreVertical className="w-5 h-5" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-4 border-t border-zinc-800 flex items-center justify-between bg-zinc-900/50 mt-auto">
                        <button
                            className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-400 transition-colors disabled:opacity-50"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage(p => p - 1)}
                        >
                            Previous
                        </button>
                        <div className="flex items-center gap-1">
                            {pageNumbers.map((p, i) =>
                                p === '...' ? (
                                    <span key={`ellipsis-${i}`} className="px-2 text-zinc-600">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p as number)}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${page === p ? 'bg-yellow-400 text-zinc-950 font-bold' : 'hover:bg-zinc-800 text-zinc-400'}`}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                        </div>
                        <button
                            className="px-4 py-2 border border-zinc-800 bg-zinc-950 rounded-xl text-sm font-medium hover:bg-zinc-800 text-zinc-300 transition-colors disabled:opacity-50"
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
